import { selectCurrentHotelId, selectHotelList, selectIsInternalAdmin } from 'accessors'
import { updateCampaign } from 'actions/campains'
import {
  PrimarySmallButton,
  SecondarySmallButton
} from 'components/ReusableComponents/Button/SmallButtons'
import ConfirmationDialog from 'components/ReusableComponents/ConfirmationDialog'
import AddLink from 'components/ReusableComponents/Icons/AddLink'
import TextInput from 'components/ReusableComponents/ManageHotel/TextInput'
import Toggle from 'components/ReusableComponents/Toggle'
import { useEngagePlusGate } from 'components/ReusableComponents/UnlockServiceDialog'
import useClickOut from 'hooks/useClickOut'
import usePlausible from 'hooks/usePlausible'
import { campaignHierarchy } from 'main/campaignHierarchy'
import { DEFAULT_SUBSIDIARY_ID } from 'main/globalVariables'
import moment from 'moment'
import { useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { useToasts } from 'react-toast-notifications'
import ReactTooltip from 'react-tooltip'
import campaignApi from 'services/api/campaign'
import {
  showTestSmsErrorToast,
  showTestSmsResultToast
} from 'services/helpers/campaignApiHelpers'

import s from './CampaignCard.module.scss'
import ExternalSmsDialog from './ExternalSmsDialog'
import { useCampaignName } from './useCampaignName'

const CampaignRow = ({
  campaigns,
  regularCampaign,
  OTACampaign,
  category,
  type,
  isDefaultCampaigns,
  isLocked,
  isImportant,
  updatedOn,
  updatedBy,
  alternateReviewLink
}) => {
  const [isTestSmsLoading, setIsTestSmsLoading] = useState(false)
  const [showTestDialog, setShowTestDialog] = useState(false)
  const [showExternalSmsDialog, setShowExternalSmsDialog] = useState(false)
  const [showDisableDialog, setShowDisableDialog] = useState(false)
  const [disableReason, setDisableReason] = useState('')

  const [campaignName, campaignDesc] = useCampaignName(regularCampaign.category)
  const { promptEngagePlusUpgrade } = useEngagePlusGate()

  const hasAlternativeReviewLink =
    regularCampaign.category === 'CheckedOutReview' && !!alternateReviewLink

  const { addToast } = useToasts()

  const userPhone = useSelector(s => s.login.currentUser.phone)

  const lockedCategories = [
    'Lost6Months',
    'Lost9Months',
    'Lost12Months',
    'Lost15Months',
    'AfterLastVisit',
    'OTAAfterVisit'
  ]

  const lockedHotels = [176, 180, 181, 182, 189]

  const currentHotelId = useSelector(selectCurrentHotelId)
  const hotelList = useSelector(selectHotelList)
  const isInternalAdmin = useSelector(selectIsInternalAdmin)
  const isHotelSetupCompleted = useSelector(s => s.hotelDetails.isPreOnboardingCompleted)

  const currentHotelDisplayId = hotelList.find(
    h => h.subsidiaryId === currentHotelId
  ).displayId

  const isEditHidden =
    lockedCategories.includes(category) &&
    lockedHotels.includes(currentHotelDisplayId) &&
    !isInternalAdmin

  const dispatch = useDispatch()

  const contentSubsidiaryId = isDefaultCampaigns ? DEFAULT_SUBSIDIARY_ID : currentHotelId

  const isActive = regularCampaign.isActive
  const disabledReason = regularCampaign.disabledReason

  const { postEvent, plausibleEvents } = usePlausible()

  const switchCampaignToggle = async () => {
    if (!isHotelSetupCompleted) {
      addToast(
        'Your initial payment is pending. Please complete it to enable campaigns.',
        {
          appearance: 'error',
          autoDismiss: true
        }
      )
      return
    }

    if (type === 'service' && promptEngagePlusUpgrade()) {
      return
    }

    if (isActive) {
      setShowDisableDialog(true)
      return
    }

    await updateCampaignStatus(true)
  }

  const updateCampaignStatus = async (reason = null) => {
    postEvent(
      isActive ? plausibleEvents.DisableCampaign : plausibleEvents.EnableCampaign,
      {
        category
      }
    )

    dispatch(
      updateCampaign(
        contentSubsidiaryId,
        campaigns.map(c => c.category),
        {
          isActive: !isActive,
          ...(reason && { disabledReason: reason })
        }
      )
    )
  }

  const OTAVariantCategory = campaignHierarchy?.[category]?.OTAVariant

  const sentTestSms = async category => {
    setIsTestSmsLoading(true)
    setShowTestDialog(false)
    campaignApi
      .sendTestSms({ contentCategory: category })
      .then(response => {
        showTestSmsResultToast(addToast, response, userPhone)
        setShowExternalSmsDialog(false)
      })
      .catch(error => {
        showTestSmsErrorToast(
          addToast,
          error,
          `Failed to send a test message to ${userPhone}`
        )
      })
      .finally(() => {
        setIsTestSmsLoading(false)
      })
  }

  const sentTestSmsToAnotherNumber = async data => {
    setIsTestSmsLoading(true)
    setShowTestDialog(false)
    campaignApi
      .sendTestSmsToAnotherNumber(data)
      .then(response => {
        showTestSmsResultToast(addToast, response, data.phoneNumber || userPhone)
        setShowExternalSmsDialog(false)
      })
      .catch(error => {
        showTestSmsErrorToast(
          addToast,
          error,
          `Failed to send a test message to ${data.phoneNumber || userPhone}`
        )
      })
      .finally(() => {
        setIsTestSmsLoading(false)
      })
  }

  const handleExternalSmsDialogClose = () => {
    setShowExternalSmsDialog(false)
  }

  const testDialogRef = useRef()
  useClickOut(testDialogRef, () => setShowTestDialog(false))

  const getTooltipText = () => {
    if (!isActive && disabledReason) {
      const formattedDate = updatedOn
        ? moment(updatedOn).format('DD/MM/YYYY HH:mm')
        : 'unknown date'
      const disabledBy = updatedBy || 'unknown user'

      return `Disabled by ${disabledBy} on ${formattedDate} because "${disabledReason}"`
    }
    return ''
  }

  return (
    <>
      <div
        className={s.card}
        title={`Updated by ${updatedBy} on ${moment(updatedOn).format(
          'DD/MM/YYYY hh:mm A'
        )}`}
        style={{ border: isImportant ? '1px solid #0079ff' : 'none' }}
        data-tip={getTooltipText()}
        data-for={`campaign-tooltip-${category}`}
      >
        {showDisableDialog && (
          <ConfirmationDialog
            title={`Disable ${campaignName}`}
            positiveMessage="Disable"
            negativeMessage="Cancel"
            theme="blacklist"
            isButtonDisabled={disableReason.length < 3}
            onReject={() => {
              setShowDisableDialog(false)
              setDisableReason('')
            }}
            onConfirm={async () => {
              await updateCampaignStatus(disableReason)
              setShowDisableDialog(false)
              setDisableReason('')
            }}
          >
            <TextInput
              className={s.disableReasonInput}
              label="Reason for disabling"
              value={disableReason}
              onChangeValue={setDisableReason}
              placeholder="Please enter a reason..."
            />
          </ConfirmationDialog>
        )}
        <div className={s.header}>
          <hgroup style={s.hgroup}>
            <h3 className={s.name}>{campaignName}</h3>
            {campaignDesc && <p className={s.description}>{campaignDesc}</p>}
          </hgroup>
          <Toggle
            className={s.toggle}
            labelClass={s.toggleLabel}
            label={isActive ? 'On' : 'Off'}
            checked={isActive}
            handleChange={switchCampaignToggle}
            disabled={isLocked}
          />
        </div>
        <div className={s.controls}>
          {hasAlternativeReviewLink && (
            <AddLink
              width={30}
              height={30}
              style={{ marginRight: 'auto', fill: '#0079ff' }}
            />
          )}
          <SecondarySmallButton
            disabled={isTestSmsLoading}
            onClick={() => {
              if (type === 'service' && promptEngagePlusUpgrade()) {
                return
              }

              if (OTACampaign) {
                setShowTestDialog(true)
              } else {
                sentTestSms(category)
              }
            }}
          >
            {isTestSmsLoading ? 'Sending...' : 'Test'}
            {showTestDialog && (
              <div className={s.testDialog} ref={testDialogRef}>
                <SecondarySmallButton onClick={() => sentTestSms(category)}>
                  Direct guest variant
                </SecondarySmallButton>
                <SecondarySmallButton onClick={() => sentTestSms(OTAVariantCategory)}>
                  OTA variant
                </SecondarySmallButton>
                {isInternalAdmin && (
                  <SecondarySmallButton onClick={() => setShowExternalSmsDialog(true)}>
                    Send to another number
                  </SecondarySmallButton>
                )}
              </div>
            )}
          </SecondarySmallButton>
          {!isEditHidden && (
            <Link
              to={`/${
                isDefaultCampaigns ? 'editDefaultCampaign' : 'editCampaign'
              }/${type}/${category}`}
              onClick={e => {
                if (type === 'service' && promptEngagePlusUpgrade()) {
                  e.preventDefault()
                }
              }}
            >
              <PrimarySmallButton>Edit content</PrimarySmallButton>
            </Link>
          )}
          {showExternalSmsDialog && (
            <ExternalSmsDialog
              sentTestSmsToAnotherNumber={sentTestSmsToAnotherNumber}
              category={category}
              OTAVariantCategory={OTAVariantCategory}
              isTestSmsLoading={isTestSmsLoading}
              handleClose={handleExternalSmsDialogClose}
            />
          )}
        </div>
      </div>
      {!isActive && disabledReason && (
        <ReactTooltip
          id={`campaign-tooltip-${category}`}
          place="top"
          type="dark"
          effect="solid"
          className={s.tooltip}
        />
      )}
    </>
  )
}

export default CampaignRow
